import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  createOrGetUser,
  getUser,
  getContent,
  getProgress,
  markProgress as apiMarkProgress,
  toggleBookmark as apiToggleBookmark,
  listBookmarks,
} from "@/lib/api";

import { getLMSStudentName } from "../lib/scorm";

const LearnContext = createContext(null);

const STORAGE_USER_ID = "steadfast.user.id";
const STORAGE_THEME = "steadfast.theme";

export function LearnProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(null);
  const [progress, setProgress] = useState({ subtopics: [], quizzes: [] });
  const [bookmarks, setBookmarks] = useState([]);
  const [theme, setThemeState] = useState("light");

  // Init theme
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.classList.remove("dark");
    }
  }, []);

  const setTheme = useCallback(() => {}, []);
  const toggleTheme = useCallback(() => {}, []);

  // Load content on mount
  useEffect(() => {
    getContent().then(setContent).catch(() => setContent(null));
  }, []);

  const registerUser = useCallback(async (name) => {
    const u = await createOrGetUser(name);
    localStorage.setItem(STORAGE_USER_ID, u.id);
    setUser(u);
    const [p, bks] = await Promise.all([getProgress(u.id), listBookmarks(u.id)]);
    setProgress(p);
    setBookmarks(bks);
    return u;
  }, []);

  // Restore session
  useEffect(() => {
    (async () => {
      try {
        const lmsName = getLMSStudentName();
        const uid = localStorage.getItem(STORAGE_USER_ID);

        if (uid) {
          try {
            const u = await getUser(uid);
            if (lmsName && u.name !== lmsName) {
              localStorage.removeItem(STORAGE_USER_ID);
            } else {
              setUser(u);
              const [p, bks] = await Promise.all([getProgress(u.id), listBookmarks(u.id)]);
              setProgress(p);
              setBookmarks(bks);
              setLoading(false);
              return;
            }
          } catch (e) {
            localStorage.removeItem(STORAGE_USER_ID);
          }
        }

        if (lmsName) {
          await registerUser(lmsName);
        }
      } catch (e) {
        localStorage.removeItem(STORAGE_USER_ID);
      } finally {
        setLoading(false);
      }
    })();
  }, [registerUser]);

  const markProgress = useCallback(
    async (module_id, subtopic_id) => {
      if (!user) return;
      await apiMarkProgress(user.id, module_id, subtopic_id);
      const p = await getProgress(user.id);
      setProgress(p);
    },
    [user]
  );

  const recordQuiz = useCallback(
    async () => {
      if (!user) return;
      const p = await getProgress(user.id);
      setProgress(p);
    },
    [user]
  );

  const toggleBookmark = useCallback(
    async (module_id, subtopic_id, subtopic_title) => {
      if (!user) return;
      await apiToggleBookmark(user.id, module_id, subtopic_id, subtopic_title);
      const bks = await listBookmarks(user.id);
      setBookmarks(bks);
    },
    [user]
  );

  const isSubtopicDone = useCallback(
    (module_id, subtopic_id) =>
      progress.subtopics.some((s) => s.module_id === module_id && s.subtopic_id === subtopic_id),
    [progress]
  );

  const isBookmarked = useCallback(
    (module_id, subtopic_id) =>
      bookmarks.some((b) => b.module_id === module_id && b.subtopic_id === subtopic_id),
    [bookmarks]
  );

  const getQuizResult = useCallback(
    (module_id, quiz_id) =>
      progress.quizzes.find((q) => q.module_id === module_id && q.quiz_id === quiz_id) || null,
    [progress]
  );

  // Module progress %: subtopics done + quizzes attempted
  const getModuleProgress = useCallback(
    (module) => {
      if (!module) return 0;
      const totalUnits = module.subtopics.length + module.quizzes.length;
      const doneSub = module.subtopics.filter((s) => isSubtopicDone(module.id, s.id)).length;
      const doneQz = module.quizzes.filter((q) => !!getQuizResult(module.id, q.id)).length;
      return Math.round(((doneSub + doneQz) / totalUnits) * 100);
    },
    [isSubtopicDone, getQuizResult]
  );

  const overallProgress = useCallback(() => {
    if (!content?.modules?.length) return 0;
    const total = content.modules.reduce((a, m) => a + m.subtopics.length + m.quizzes.length, 0);
    const done = content.modules.reduce((a, m) => {
      const doneSub = m.subtopics.filter((s) => isSubtopicDone(m.id, s.id)).length;
      const doneQz = m.quizzes.filter((q) => !!getQuizResult(m.id, q.id)).length;
      return a + doneSub + doneQz;
    }, 0);
    return Math.round((done / total) * 100);
  }, [content, isSubtopicDone, getQuizResult]);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_USER_ID);
    setUser(null);
    setProgress({ subtopics: [], quizzes: [] });
    setBookmarks([]);
  }, []);

  return (
    <LearnContext.Provider
      value={{
        user,
        loading,
        content,
        progress,
        bookmarks,
        theme,
        setTheme,
        toggleTheme,
        registerUser,
        markProgress,
        recordQuiz,
        toggleBookmark,
        isSubtopicDone,
        isBookmarked,
        getQuizResult,
        getModuleProgress,
        overallProgress,
        logout,
      }}
    >
      {children}
    </LearnContext.Provider>
  );
}

export const useLearn = () => {
  const ctx = useContext(LearnContext);
  if (!ctx) throw new Error("useLearn must be used inside LearnProvider");
  return ctx;
};
